import React, { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  Menu,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import DashboardIcon from "@mui/icons-material/Dashboard";
import MonetizationOnIcon from "@mui/icons-material/MonetizationOn";
import CommentIcon from "@mui/icons-material/Comment";
import SearchIcon from "@mui/icons-material/Search";
import PeopleIcon from "@mui/icons-material/People";
import MapIcon from "@mui/icons-material/Map";
import ArticleIcon from "@mui/icons-material/Article";
import SettingsIcon from "@mui/icons-material/Settings";
import AssessmentIcon from "@mui/icons-material/Assessment";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import MonitorIcon from "@mui/icons-material/Monitor";
import PaymentIcon from "@mui/icons-material/Payment";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import GavelIcon from "@mui/icons-material/Gavel";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import { useAuth } from "../contexts/AuthContext";
import logo from "../assets/5bc.png";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { useTranslation } from "react-i18next";
import i18n from "i18next";

const auctionMenuItems: { path: string; label: string; icon: React.ReactNode; guestOnly?: boolean }[] = [
  { path: "/", label: "Active Auctions", icon: <GavelIcon /> },
  { path: "/create", label: "Create Auction", icon: <AddCircleOutlineIcon /> },
  { path: "/my-pending-auctions", label: "My Pending Auctions", icon: <PendingActionsIcon /> },
  { path: "/exchange", label: "P2P Exchange", icon: <SwapHorizIcon /> },
  { path: "/blog", label: "Blog", icon: <ArticleIcon /> },
  { path: "/login", label: "Login", icon: <LoginIcon />, guestOnly: true },
  { path: "/register", label: "Register", icon: <PersonAddIcon />, guestOnly: true },
];

const adminMenuItems: { path: string; label: string; icon: React.ReactNode }[] = [
  { path: "/admin", label: "Dashboard", icon: <DashboardIcon /> },
  { path: "/admin/live", label: "Live Auctions", icon: <MonetizationOnIcon /> },
  { path: "/admin/escrows", label: "Escrow Transactions", icon: <MonetizationOnIcon /> },
  { path: "/admin/comments", label: "Moderate Comments", icon: <CommentIcon /> },
  { path: "/admin/currencies", label: "Currencies", icon: <MonetizationOnIcon /> },
  { path: "/admin/peb-search", label: "PEB Search", icon: <SearchIcon /> },
  { path: "/admin/users", label: "Users", icon: <PeopleIcon /> },
  { path: "/admin/gis", label: "GIS Manager", icon: <MapIcon /> },
  { path: "/admin/blogs", label: "Blog Management", icon: <ArticleIcon /> },
  { path: "/admin/popup-content", label: "Popup Content", icon: <SettingsIcon /> },
  { path: "/admin/logs", label: "System Logs", icon: <AssessmentIcon /> },
  { path: "/admin/whatsapp", label: "WhatsApp Messaging", icon: <WhatsAppIcon /> },
  { path: "/admin/whatsapp-monitor", label: "WhatsApp Monitor", icon: <MonitorIcon /> },
  { path: "/test-payment", label: "Test Payment", icon: <PaymentIcon /> },
];

const Navbar = () => {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [auctionAnchorEl, setAuctionAnchorEl] = useState<null | HTMLElement>(null);
  const [adminAnchorEl, setAdminAnchorEl] = useState<null | HTMLElement>(null);
  const auctionMenuOpen = Boolean(auctionAnchorEl);
  const adminMenuOpen = Boolean(adminAnchorEl);

  const handleAuctionMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAuctionAnchorEl(event.currentTarget);
  };
  const handleAuctionMenuClose = () => setAuctionAnchorEl(null);
  const handleAuctionNavigate = (path: string) => {
    navigate(path);
    handleAuctionMenuClose();
  };

  const handleAdminMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAdminAnchorEl(event.currentTarget);
  };
  const handleAdminMenuClose = () => setAdminAnchorEl(null);
  const handleAdminNavigate = (path: string) => {
    navigate(path);
    handleAdminMenuClose();
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <Box sx={{ display: "flex", alignItems: "center", mr: 2 }}>
          <img
            src={logo}
            alt="Florida Gateway Auction Logo"
            style={{ height: 48, marginRight: 12 }}
          />
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexGrow: 1, alignItems: "center" }}>
          <Button
            color="inherit"
            onClick={handleAuctionMenuOpen}
            endIcon={<ArrowDropDownIcon />}
            aria-controls={auctionMenuOpen ? "auction-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={auctionMenuOpen ? "true" : undefined}
            sx={{ fontWeight: 700 }}
          >
            {t("auction") || "Auction"}
          </Button>
          <Menu
            id="auction-menu"
            anchorEl={auctionAnchorEl}
            open={auctionMenuOpen}
            onClose={handleAuctionMenuClose}
            MenuListProps={{ "aria-labelledby": "auction-menu-button" }}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            {auctionMenuItems
              .filter((item) => !item.guestOnly || !user)
              .map((item) => (
                <MenuItem key={item.path} onClick={() => handleAuctionNavigate(item.path)}>
                  <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.path === "/login" ? (t("login") || item.label) : item.path === "/register" ? (t("register.title") || item.label) : item.label}
                  />
                </MenuItem>
              ))}
          </Menu>
          <Button
            color="inherit"
            component="a"
            href={
              user && user.token
                ? `https://karaoke.5bc.us/karaoke?token=${encodeURIComponent(user.token)}`
                : "https://karaoke.5bc.us/karaoke"
            }
            target="_blank"
            rel="noopener noreferrer"
          >
            🎤 Karaoke
          </Button>
          {user ? (
            <>
              {(user.role === "admin" || user.role === "blog") && (
                <>
                  <Button
                    color="inherit"
                    onClick={handleAdminMenuOpen}
                    endIcon={<ArrowDropDownIcon />}
                    aria-controls={adminMenuOpen ? "admin-menu" : undefined}
                    aria-haspopup="true"
                    aria-expanded={adminMenuOpen ? "true" : undefined}
                  >
                    {t("admin") || "Admin"}
                  </Button>
                  <Menu
                    id="admin-menu"
                    anchorEl={adminAnchorEl}
                    open={adminMenuOpen}
                    onClose={handleAdminMenuClose}
                    MenuListProps={{ "aria-labelledby": "admin-menu-button" }}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    {adminMenuItems
                      .filter((item) => user.role === "admin" || (user.role === "blog" && ["/admin", "/admin/blogs"].includes(item.path)))
                      .map((item) => (
                        <MenuItem key={item.path} onClick={() => handleAdminNavigate(item.path)}>
                          <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                          <ListItemText>{item.label}</ListItemText>
                        </MenuItem>
                      ))}
                  </Menu>
                </>
              )}
            </>
          ) : (
            <>
              <Button color="inherit" component={RouterLink} to="/login">
                {t("login") || "Login"}
              </Button>
              <Button color="inherit" component={RouterLink} to="/register">
                {t("register.title")}
              </Button>
            </>
          )}
        </Box>

        {/* Language Switcher */}
        <Box sx={{ display: "flex", alignItems: "center", mr: 2, gap: 1 }}>
          <Typography variant="body2" sx={{ color: "inherit", whiteSpace: "nowrap" }}>
            {t("language") || "Language"}
          </Typography>
          <FormControl size="small" sx={{ minWidth: 72 }}>
            <Select
              value={i18n.language}
              onChange={(e) => i18n.changeLanguage(e.target.value)}
              aria-label="Select language"
              sx={{
                color: "inherit",
                ".MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.5)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(255,255,255,0.8)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                "& .MuiSvgIcon-root": { color: "white" },
              }}
              variant="outlined"
            >
              <MenuItem value="en">EN</MenuItem>
              <MenuItem value="es">ES</MenuItem>
              <MenuItem value="it">IT</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {user && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="body1" sx={{ color: "white" }}>
              {t("welcome") || "Welcome"}, {user.username}!
            </Typography>
            <IconButton
              color="inherit"
              component={RouterLink}
              to="/profile"
              sx={{ ml: 1 }}
            >
              <AccountCircleIcon />
            </IconButton>
            <Button color="inherit" onClick={logout}>
              {t("logout") || "Logout"}
            </Button>
          </Box>
        )}
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
